// ============================================================
// AddressLookup.tsx
// Type-as-you-go address autocomplete. Single text input → debounced
// suggestion fetch → keyboard-or-mouse pick → details fetch →
// onSelect callback with the 5-field structured Address.
//
// Used in two places today (ClientPickerModal create form, and the
// client home-address block in ClientDetailsSection) sitting ABOVE
// the 5 manual-edit inputs that stay editable for corrections.
//
// Dropdown close triggers (explicit):
//   (a) successful pick (handlePick clears query + suggestions)
//   (b) Escape key
//   (c) click outside the wrapper
// Otherwise the dropdown stays open while the user is interacting.
// ============================================================
import React, { useState, useEffect, useRef, useId } from 'react'
import {
  fetchAddressSuggestions,
  fetchAddressDetails,
  AddressSuggestion,
} from '../../api/addressLookup'
import { Address } from '../../types/case.types'

interface AddressLookupProps {
  onSelect: (address: Address) => void
  value: Address | null
  // Optional Clear callback. When provided + a value is set, the
  // "Clear" button inside the search input becomes enabled and
  // calls this. Callers should write empty strings to the 5
  // address fields they own — same shape as onSelect but with
  // every field cleared. When omitted, the button still renders
  // (per the always-rendered contract) but stays disabled.
  onClear?: () => void
  // 'info' — the client-info sections look (ClientInfoSections): 38px field
  // with a leading search icon, a floating suggestion popover, a one-line
  // hint, and NO mint preview (the editable address fields sit right below
  // and already show the value). Omitted → the original look, unchanged.
  variant?: 'info'
  // Input id (so a <label htmlFor> can point at it).
  inputId?: string
}

const IcSearch: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
)
const IcPin: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
)
const IcCheck: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0"><path d="M20 6 9 17l-5-5" /></svg>
)

// Don't fire a network call for trivial queries — real Google Places
// is billed per call, and 1–2 char queries surface no useful matches
// anyway. Baking this in now keeps the stub→real swap honest.
const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 250

const AddressLookup: React.FC<AddressLookupProps> = ({ onSelect, value, onClear, variant, inputId }) => {
  const info = variant === 'info'
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  // -1 means "no keyboard focus on any row"; mouse hover and arrow
  // keys both update this. Reset to -1 whenever suggestions change
  // shape so a stale highlight doesn't survive a new query.
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [loading, setLoading] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  // Stale-response guard. Every new query bumps this; suggestion +
  // details fetches commit state only if their id matches the latest.
  // Matters more once the real Google API replaces the stub.
  const requestIdRef = useRef(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // ★ (case-page audit) The details fetch is async: if this lookup unmounted
  // meanwhile (case switch / card closed), the parent's onSelect would write
  // the address into whatever record is open NOW. Drop late results.
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])
  // Stable listbox id for aria-controls / aria-activedescendant —
  // useId gives us a per-component string that survives re-renders.
  const listboxId = useId()

  // Debounced suggestion fetch.
  useEffect(() => {
    const q = query.trim()
    if (q.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setError(null)
      setHighlightedIndex(-1)
      return
    }
    const id = ++requestIdRef.current
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const results = await fetchAddressSuggestions(q)
        if (id !== requestIdRef.current) return
        setSuggestions(results)
        setError(null)
        setHighlightedIndex(results.length > 0 ? 0 : -1)
        setDropdownOpen(true)
      } catch {
        if (id !== requestIdRef.current) return
        setError('Address lookup failed. Please try again.')
        setSuggestions([])
      } finally {
        if (id === requestIdRef.current) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  // Close-on-click-outside — close trigger (c). mousedown rather than
  // click so the dropdown vanishes before the next input registers
  // its click (avoids a double-click feel for adjacent inputs).
  useEffect(() => {
    if (!dropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [dropdownOpen])

  // Close trigger (a): successful pick. Resolves the chosen placeId to
  // a full Address, hands it to the parent via onSelect, then clears
  // the search box so the user can search again to change. The mint
  // preview box below confirms the selection landed.
  const handlePick = async (suggestion: AddressSuggestion) => {
    if (resolving) return
    setResolving(true)
    setError(null)
    try {
      const address = await fetchAddressDetails(suggestion.placeId)
      if (!mountedRef.current) return
      onSelect(address)
      setQuery('')
      setSuggestions([])
      setHighlightedIndex(-1)
      setDropdownOpen(false)
    } catch {
      if (mountedRef.current) setError('Failed to load address details.')
    } finally {
      if (mountedRef.current) setResolving(false)
    }
  }

  // Close trigger (b): Escape. Keep the query so the user can edit
  // and reopen the dropdown; just hide it for now.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setDropdownOpen(false)
      setHighlightedIndex(-1)
      return
    }
    if (!dropdownOpen || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0) {
        void handlePick(suggestions[highlightedIndex])
      }
    }
  }

  const formatAddress = (addr: Address): string =>
    [addr.line1, addr.line2, addr.town, addr.county, addr.postcode]
      .filter(Boolean)
      .join(', ')

  const trimmedQuery = query.trim()
  const showNoMatches =
    dropdownOpen &&
    !loading &&
    !error &&
    suggestions.length === 0 &&
    trimmedQuery.length >= MIN_QUERY_LENGTH

  // Clear button — disabled when nothing to clear OR no onClear
  // handler wired up. "Nothing to clear" = value is null OR every
  // one of the 5 fields is empty after trim. Render always (per the
  // brief's contract) so layout stays stable; the disabled state
  // communicates "no action available" via the standard disabled
  // styling rather than by hiding the button.
  const hasContent =
    value !== null &&
    (!!value.line1 || !!value.line2 || !!value.town ||
     !!value.county || !!value.postcode)
  const clearDisabled = !onClear || !hasContent

  const inputClass = info
    ? 'w-full h-[38px] rounded-[9px] border border-casevo-border bg-casevo-input text-casevo-forest pl-9 pr-20 text-[14px] outline-none transition-[border-color,box-shadow,background-color] focus:border-casevo-mid focus:bg-casevo-surface focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_20%,transparent)] placeholder:text-casevo-text-soft'
    : 'w-full border border-casevo-border rounded px-3 py-2 pr-20 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid'

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        {info && <span className="absolute left-[11px] top-[11px] text-casevo-mid pointer-events-none"><IcSearch /></span>}
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={dropdownOpen}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          placeholder={info ? 'Start typing a postcode or the first line…' : 'Search for an address…'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setDropdownOpen(true)
          }}
          onFocus={() => {
            if (suggestions.length > 0) setDropdownOpen(true)
          }}
          onKeyDown={handleKeyDown}
          className={inputClass}
        />
        {(loading || resolving) && (
          // Shifted to right-16 so the Clear button (right-3) doesn't
          // overlap during the brief moments both render at once.
          <span
            className="absolute right-16 top-1/2 -translate-y-1/2 text-xs text-casevo-mid italic pointer-events-none"
            aria-live="polite"
          >
            {resolving ? 'Loading…' : 'Searching…'}
          </span>
        )}
        <button
          type="button"
          onClick={() => onClear?.()}
          disabled={clearDisabled}
          aria-label="Clear address"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-casevo-mid hover:text-casevo-forest text-xs uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Clear
        </button>
      </div>
      {error && <p className="text-casevo-danger text-xs mt-1">{error}</p>}
      {dropdownOpen && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className={info
            ? 'absolute left-0 right-0 top-[42px] z-20 bg-casevo-surface border border-casevo-border rounded-[10px] p-[5px] shadow-[0_12px_32px_rgba(16,42,29,0.16)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.45)]'
            : 'mt-1 border border-casevo-border rounded bg-casevo-surface shadow-sm overflow-hidden'}
        >
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              id={`${listboxId}-option-${i}`}
              role="option"
              aria-selected={i === highlightedIndex}
              // mousedown not click so the input doesn't lose focus
              // and trigger close-on-blur before this fires.
              onMouseDown={(e) => {
                e.preventDefault()
                void handlePick(s)
              }}
              onMouseEnter={() => setHighlightedIndex(i)}
              className={
                (info ? 'px-[10px] py-2 rounded-[7px] text-[13.5px] flex gap-2 items-center cursor-pointer ' : 'px-3 py-2 text-sm cursor-pointer ') +
                (i === highlightedIndex
                  ? 'bg-casevo-mint text-casevo-forest'
                  : 'text-casevo-forest hover:bg-casevo-mint')
              }
            >
              {info && <IcPin />}
              {s.description}
            </li>
          ))}
        </ul>
      )}
      {showNoMatches && (
        <p className="text-xs text-casevo-mid italic mt-1">No matches found.</p>
      )}
      {info && (
        <p className="text-[12px] text-casevo-text-soft mt-1.5 flex items-center gap-1.5"><IcCheck />Pick a result to fill the fields below — or edit them directly.</p>
      )}
      {!info && value && (
        <div className="text-sm text-casevo-forest bg-casevo-mint border border-casevo-border rounded px-3 py-2 mt-2">
          {formatAddress(value)}
        </div>
      )}
    </div>
  )
}

export default AddressLookup
